import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { Layout } from '../components/layout';
import { Button, ImageWithLoader } from '../components/ui';
import { CashbackBadge } from '../components/CashbackBadge';
import { api } from '../services/api';
import { optimizeImage } from '../utils/image';
import { useCartStore } from '../store';
import { Product, ProductVariant } from '../types';
import { formatPrice } from '../utils/helpers';
import {
  SHARING_TYPE_TITLE,
  cheapestOffer,
  isLiveOffer,
  offerHint,
  offerLabel,
  sharingOffers,
  sharingTypeOf,
} from '../utils/sharing';

const H2 = { fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '17px', lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' } as const;
const MUTED = { fontSize: '14px', lineHeight: '19px', color: '#818384' } as const;

/** Карточка товара шеринга: плитки позиций (П2/П3/Т3) или консоли у подписки, корзина, пояснения */
export const SharingProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem, itemsByRegion } = useCartStore();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    api.getProduct(id).then((p) => {
      if (cancelled) return;
      if (p && p.category === 'sharing') {
        setProduct(p);
        setSelectedId(cheapestOffer(p)?.id ?? null);
      } else {
        setProduct(null);
      }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const type = product ? sharingTypeOf(product) : 'game';
  const isSub = type === 'subscription';
  const offers = useMemo(() => (product ? sharingOffers(product) : []), [product]);
  const selected: ProductVariant | null = offers.find((v) => v.id === selectedId && isLiveOffer(v)) || (product ? cheapestOffer(product) : null);
  const inCart = !!selected && (itemsByRegion.SH || []).some((i) => i.variant.id === selected.id);
  const platforms = Array.from(new Set(offers.map((v) => v.platform).filter(Boolean))).join(', ');
  const language = offers.find((v) => v.language)?.language as string | undefined;

  const handleAdd = async () => {
    if (!product || !selected) return;
    if (inCart) { navigate('/cart'); return; }
    await addItem(product, selected);
  };

  if (loading) {
    return (
      <Layout showNav={false}>
        <div className="w-full aspect-square bg-gray-200/70 animate-pulse" />
        <div className="px-4 py-5 space-y-3">
          <div className="h-6 w-2/3 rounded bg-gray-200/70 animate-pulse" />
          <div className="h-20 rounded-2xl bg-gray-200/70 animate-pulse" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout showNav={false}>
        <div className="px-4 py-6">
          <p style={MUTED}>Товар не найден</p>
          <Button onClick={() => navigate('/sharing')} className="mt-4">В раздел «Шеринг»</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showNav={false}>
      <div className="pb-20">
        <div className="relative">
          <ImageWithLoader
            src={optimizeImage(product.coverImage, 800)}
            alt={product.name}
            wrapperClassName="w-full aspect-square"
            className="object-cover object-top w-full"
            style={{ maxHeight: '55vh' }}
            spinnerSize={32}
            loading="eager"
            fallbackSrc="https://via.placeholder.com/400x400?text=PS"
          />
        </div>

        <div className="bg-white rounded-t-2xl pt-5 pb-5 px-4 relative z-10 -mt-5">
          <p className="mb-1" style={{ fontSize: '12px', lineHeight: '16px', color: '#818384' }}>Шеринг · {SHARING_TYPE_TITLE[type]}</p>
          <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '22px', fontWeight: 700, lineHeight: '120%', color: '#1A1D1A' }}>{product.name}</h1>
          {product.subtitle && <p className="mt-0.5" style={{ fontSize: '13px', lineHeight: '16px', color: '#818384' }}>{product.subtitle}</p>}

          {selected && (
            <div className="flex items-center flex-wrap gap-2.5 mt-3 mb-4">
              <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '34px', lineHeight: '120%', color: '#1A1D1A' }}>{formatPrice(selected.price)}</span>
              {selected.oldPrice && selected.oldPrice > selected.price && (
                <span className="line-through" style={{ fontSize: '15px', color: '#B0B3BA' }}>{formatPrice(selected.oldPrice)}</span>
              )}
              <CashbackBadge price={selected.price} />
            </div>
          )}

          <h2 className="mb-2" style={H2}>{isSub ? 'Консоль' : 'Выберите позицию'}</h2>
          <div className={`grid gap-2 ${offers.length >= 3 ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {offers.map((v) => {
              const live = isLiveOffer(v);
              const active = selected?.id === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={!live}
                  onClick={() => live && setSelectedId(v.id)}
                  className="text-left rounded-2xl p-3.5 transition-all"
                  style={{
                    border: `1.5px solid ${!live ? '#E1E3E5' : active ? '#3657F0' : '#E1E3E5'}`,
                    background: !live ? '#F5F7FA' : active ? '#EEF2FF' : '#FFFFFF',
                    opacity: live ? 1 : 0.7,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '15px', color: active ? '#3657F0' : '#1A1D1A' }}>
                      {offerLabel(type, v)}
                      {!isSub && <span style={{ fontWeight: 500, color: '#818384' }}> · {v.platform}</span>}
                    </span>
                    {live && v.discount ? (
                      <span className="px-1.5 py-0.5 rounded-md text-white text-[11px] font-bold" style={{ background: '#3657F0' }}>−{v.discount}%</span>
                    ) : null}
                  </div>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '20px', lineHeight: '24px', color: live ? '#1A1D1A' : '#818384' }}>
                      {live ? formatPrice(v.price) : 'Нет в наличии'}
                    </span>
                    {live && v.oldPrice && v.oldPrice > v.price && (
                      <span className="line-through" style={{ fontSize: '12px', color: '#B0B3BA' }}>{formatPrice(v.oldPrice)}</span>
                    )}
                  </div>
                  <p className="mt-1" style={{ fontSize: '12px', lineHeight: '16px', color: '#818384' }}>{offerHint(type, v)}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-5 rounded-2xl" style={{ background: '#F5F7FA' }}>
            <Row label="Платформа" value={platforms || '—'} />
            {language && <Row label="Язык" value={language} />}
            {product.subtitle && <Row label="Издание" value={product.subtitle} />}
            <Row label="Выдача" value="Вручную, через поддержку" last />
          </div>

          {product.description && (
            <div className="mt-5">
              <h2 className="mb-2" style={H2}>Состав издания</h2>
              <div className="rounded-2xl p-4 whitespace-pre-line" style={{ ...MUTED, background: '#F5F7FA' }}>{product.description}</div>
            </div>
          )}

          <div className="mt-5 rounded-2xl p-4" style={{ border: '1px solid #E1E3E5' }}>
            {isSub ? (
              <>
                <h2 className="mb-2" style={H2}>Что такое шеринг подписки?</h2>
                <p style={MUTED}>
                  Это общий аккаунт с подпиской, которым пользуетесь вы и еще один покупатель. Каждый играет
                  со своего профиля и не мешает другому: доступен весь каталог подписки, включая онлайн-игру.
                  Вместе с аккаунтом мы присылаем понятную инструкцию, а на весь срок подписки действует гарантия.
                </p>
                <h3 className="mt-3 mb-1" style={{ ...H2, fontSize: '15px' }}>Правила пользования</h3>
                <ul className="list-disc pl-5 space-y-1" style={MUTED}>
                  <li>Играть можно только со своего профиля — в выданный аккаунт вы входите один раз, для активации.</li>
                  <li>Не меняйте данные выданного аккаунта: почту, пароль и настройки консоли.</li>
                  <li>Если на игре появился замок, перезапустите ее или откройте инструкцию — это решается за полминуты.</li>
                </ul>
              </>
            ) : (
              <>
                <h2 className="mb-2" style={H2}>Что такое позиции?</h2>
                <p style={MUTED}>Мы выдаем доступ к аккаунту, на котором уже куплена игра. Аккаунт остается у вас, а вы выбираете, как играть.</p>
                <div className="mt-3 space-y-3">
                  <Explain code="П2" title="Играете на выданном аккаунте" text="Входите в выданный аккаунт на своей консоли, скачиваете игру и играете прямо с него. Самый выгодный вариант." />
                  <Explain code="П3" title="Играете на своем аккаунте" text="Входите в выданный аккаунт, включаете общий доступ к консоли, скачиваете игру и запускаете ее уже со своего профиля — с вашими трофеями и сохранениями." />
                  {type === 'game_crossgen' && (
                    <Explain code="Т3" title="То же, что П3, но на PS4" text="Версия игры для PS4: играете со своего профиля, включив общий доступ на консоли." />
                  )}
                </div>
              </>
            )}
            <p className="mt-3" style={{ fontSize: '12px', lineHeight: '16px', color: '#818384' }}>
              После оплаты напишите нам в поддержку — выдача ручная, пришлем данные аккаунта и инструкцию.
            </p>
          </div>
        </div>

        <div
          className="fixed left-0 right-0 z-30 bg-white"
          style={{
            bottom: '0',
            paddingBottom: Math.max((WebApp as any).safeAreaInset?.bottom || 0, (WebApp as any).contentSafeAreaInset?.bottom || 0, 8),
          }}
        >
          <div className="flex items-center gap-3" style={{ padding: '8px 16px 0 16px' }}>
            {selected && (
              <div className="flex-shrink-0">
                <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: '18px', lineHeight: '22px', color: '#1A1D1A' }}>{formatPrice(selected.price)}</p>
                <p style={{ fontSize: '11px', lineHeight: '14px', color: '#818384' }}>{offerLabel(type, selected)}{!isSub ? ` · ${selected.platform}` : ''}</p>
              </div>
            )}
            <div className="flex-1">
              <Button
                onClick={handleAdd}
                disabled={!selected}
                fullWidth
                size="md"
                style={{ height: '52px', fontSize: '16px', fontWeight: 600, borderRadius: '16px' }}
              >
                {!selected ? 'Нет в наличии' : inCart ? 'Перейти в корзину' : 'Добавить в корзину'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const Row: React.FC<{ label: string; value: string; last?: boolean }> = ({ label, value, last }) => (
  <div className="flex items-center justify-between gap-4 px-4 py-3" style={{ borderBottom: last ? 'none' : '1px solid #E1E3E5' }}>
    <span style={MUTED}>{label}</span>
    <span className="text-right" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '19px', color: '#1A1D1A' }}>{value}</span>
  </div>
);

const Explain: React.FC<{ code: string; title: string; text: string }> = ({ code, title, text }) => (
  <div className="flex gap-3">
    <span className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-extrabold" style={{ background: '#3657F0', fontFamily: 'Manrope, sans-serif' }}>{code}</span>
    <div>
      <p style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '15px', lineHeight: '20px', color: '#1A1D1A' }}>{title}</p>
      <p style={{ fontSize: '13px', lineHeight: '18px', color: '#818384' }}>{text}</p>
    </div>
  </div>
);
